import { useEffect, useState } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useIdleTimerContext } from 'react-idle-timer';

import { GqlDispensaries, useCreatePublicContextSessionMutation } from 'types/graphql';
import useUI from 'src/hooks/use-ui';
import useUser from 'src/hooks/use-user';
import useCart from 'shared/hooks/use-cart';
import { useGetSessionId } from '../kiosk-pro-shared/use-session-id';

type UseKioskProLayoutProps = {
  dispensary: GqlDispensaries;
};

type UseKioskProLayoutReturn = {
  isConsumerPhoneLoginPage: boolean;
  startNewSession: () => void;
  goToCart: () => void;
  kioskProModalLogin: () => Promise<void>;
  createPublicContextSessionLoading: boolean;
  isKioskProLoginModalOpen: boolean;
  publicLoginSessionId: string;
  setIsKioskProLoginModalOpen: (open: boolean) => void;
  consumerPhoneLoginUrl: string;
  phoneLoginUrl: string;
  isEndSessionModalOpen: boolean;
  setIsEndSessionModalOpen: (open: boolean) => void;
  confirmedStartNewSession: () => Promise<void>;
};

export const useKioskProLayout = ({ dispensary }: UseKioskProLayoutProps): UseKioskProLayoutReturn => {
  const { setIsKioskProLoginModalOpen, isKioskProLoginModalOpen, dispensaryRootUrl, openCartDrawer } = useUI();
  const User = useUser();
  const router = useRouter();
  const Cart = useCart();
  const cName = dispensary.cName ?? '';
  const dispensaryId = dispensary.id;
  const idleTimer = useIdleTimerContext();
  const isUserLoggedIn = useObserver(() => User?.isLoggedIn);

  const [consumerPhoneLoginUrl, setConsumerPhoneLoginUrl] = useState('');
  const [isEndSessionModalOpen, setIsEndSessionModalOpen] = useState(false);
  const publicLoginSessionId = useGetSessionId();
  const [
    createPublicContextSessionMutation,
    { loading: createPublicContextSessionLoading },
  ] = useCreatePublicContextSessionMutation();

  const CONSUMER_LOGIN_PATHS = ['login/public-session-login', 'login/public-session-loyalty-signup'];
  const isConsumerPhoneLoginPage = useObserver(() =>
    CONSUMER_LOGIN_PATHS.some((path) => router.pathname.includes(path))
  );

  // TODO: Fix this phone login page when we get a clear direction from the product team
  const phoneLoginUrl = `/kiosks/${cName}/login/public-session-continue?session-id=${publicLoginSessionId}`;

  useEffect(() => {
    if (typeof window !== 'undefined' && cName) {
      setConsumerPhoneLoginUrl(
        `${window.location.origin}/kiosks/${cName}/login/public-session-login?session-id=${publicLoginSessionId}`
      );
    }
  }, [cName, publicLoginSessionId]);

  const startNewSession = (): void => {
    setIsEndSessionModalOpen(true);
  };

  const confirmedStartNewSession = async (): Promise<void> => {
    try {
      setIsKioskProLoginModalOpen(false);
      Cart?.clearOrder();
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      idleTimer?.reset();
      User?.setKioskGuestPhoneNumber(null);

      if (isUserLoggedIn) {
        await User?.logout();
      }

      void router.push(
        `/${String(dispensaryRootUrl)}/[cName]/lock`,
        `/${String(dispensaryRootUrl)}/${String(cName)}/lock`
      );
    } catch (error) {
      console.error('Failed to start new session:', error);
    }
  };

  const goToCart = (): void => {
    openCartDrawer();
  };

  const kioskProModalLogin = async (): Promise<void> => {
    if (!dispensaryId) {
      return;
    }

    const result = await createPublicContextSessionMutation({
      variables: {
        dispensaryId,
      },
    });

    const sessionId = result.data?.createPublicContextSession?.id;

    if (sessionId) {
      const currentQuery = { ...router.query };
      currentQuery['session-id'] = sessionId;

      void router.push({
        pathname: router.pathname,
        query: currentQuery,
      });
    }

    setIsKioskProLoginModalOpen(true);
  };

  return {
    isConsumerPhoneLoginPage,
    startNewSession,
    goToCart,
    kioskProModalLogin,
    createPublicContextSessionLoading,
    isKioskProLoginModalOpen,
    publicLoginSessionId,
    setIsKioskProLoginModalOpen,
    consumerPhoneLoginUrl,
    phoneLoginUrl,
    isEndSessionModalOpen,
    setIsEndSessionModalOpen,
    confirmedStartNewSession,
  };
};
